import classNames from 'classnames';
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  format,
  parseISO,
} from 'date-fns';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useMemo, VFC } from 'react';

import {
  isAnnouncement,
  isPurchase,
  isReleaseReminder,
  Notification,
} from '@/apis/aniftyBackend/notifications/interface';
import { weiToEth } from '@/apis/blockchain/blockchainCommon';
import routes from '@/config/routes';
import { DEFAULT_PROFILE_IMAGE_PATH } from '@/constants/images';
import generateSquareImageURL from '@/utils/generateSquareImageURL';
import { convertImgixURL } from '@/utils/imgixUtils';

import styles from './NotificationItem.module.scss';

type Props = {
  notification: Notification;
};

const NotificationItem: VFC<Props> = ({ notification }: Props) => {
  const { i18n } = useTranslation();
  const isJa = i18n.language === 'ja';

  const displayTime = useMemo(() => {
    const parsedCreatedAt = parseISO(notification.createdAt);

    // transition "few seconds ago" -> "~ minutes ago" -> "~ hours ago" -> "Feb 13, 2022 at 3:47 PM"
    if (differenceInMinutes(new Date(), parsedCreatedAt) < 1) {
      return isJa ? '数秒前' : 'few seconds ago';
    } else if (differenceInHours(new Date(), parsedCreatedAt) < 1) {
      return isJa
        ? `${differenceInMinutes(new Date(), parsedCreatedAt)}分前`
        : `${differenceInMinutes(new Date(), parsedCreatedAt)} minutes ago`;
    } else if (differenceInDays(new Date(), parsedCreatedAt) < 1) {
      return isJa
        ? `${differenceInHours(new Date(), parsedCreatedAt)}時間前`
        : `${differenceInHours(new Date(), parsedCreatedAt)} hours ago`;
    } else {
      // return format(parsedCreatedAt, "LLL d, y 'at' K:mm a");
      return isJa
        ? format(parsedCreatedAt, "y'年'MM'月'd'日'H:mm")
        : format(parsedCreatedAt, "LLL d, y 'at' K:mm a");
    }
  }, [notification.createdAt]);

  /**
   * Render for each notification item
   */

  // Announcement
  if (isAnnouncement(notification.notificationData)) {
    const announcement = notification.notificationData;

    return (
      <div
        className={classNames(styles.notificationItemContainer, {
          [styles.isRead]: !!notification.openedAt,
        })}
      >
        <div className={styles.notificationItem}>
          <img src={DEFAULT_PROFILE_IMAGE_PATH} />
          <div className={styles.notificationContent}>
            <p className={styles.notificationType}>ANNOUNCEMENT</p>
            <p className={styles.notificationBody}>
              {isJa ? announcement.contentJA : announcement.content}
            </p>
            <p className={styles.notificationDateTime}>{displayTime}</p>
          </div>
        </div>
      </div>
    );
  }

  // Purchase
  if (isPurchase(notification.notificationData)) {
    const purchase = notification.notificationData;

    const creationName =
      isJa && purchase.creationNameJA
        ? purchase.creationNameJA
        : purchase.creationName;

    const squareImageURL = generateSquareImageURL(
      convertImgixURL(purchase.creationImageURL)
    );

    return (
      <Link href={routes.creationDetail(purchase.creationID)}>
        <a
          className={classNames(styles.notificationItemContainer, {
            [styles.isRead]: !!notification.openedAt,
          })}
        >
          <div className={styles.notificationItem}>
            <img src={squareImageURL} />
            <div className={styles.notificationContent}>
              <p className={styles.notificationType}>PURCHASE</p>
              <p className={styles.notificationBody}>
                {isJa
                  ? `${creationName} が ${weiToEth(
                      purchase.price
                    )} ETHで売れました！ 販売数は残り${
                      purchase.availableTokenAmount
                    }個です。`
                  : `${creationName} is bought at ${weiToEth(
                      purchase.price
                    )} ETH. You
              got ${purchase.availableTokenAmount} left in suplies.`}
              </p>
              <p className={styles.notificationDateTime}>{displayTime}</p>
            </div>
          </div>
        </a>
      </Link>
    );
  }

  // Release reminder
  if (isReleaseReminder(notification.notificationData)) {
    const releaseReminder = notification.notificationData;

    const creationName =
      isJa && releaseReminder.creationNameJA
        ? releaseReminder.creationNameJA
        : releaseReminder.creationName;

    const squareImageURL = generateSquareImageURL(
      convertImgixURL(releaseReminder.creationImageURL)
    );

    return (
      <Link href={routes.creationDetail(releaseReminder.creationID)}>
        <a
          className={classNames(styles.notificationItemContainer, {
            [styles.isRead]: !!notification.openedAt,
          })}
        >
          <div className={styles.notificationItem}>
            <img src={squareImageURL} />
            <div className={styles.notificationContent}>
              <p className={styles.notificationType}>RELEASE REMINDER</p>
              <p className={styles.notificationBody}>
                {isJa
                  ? `@${
                      releaseReminder.userName
                    } さんが ${creationName} を${weiToEth(
                      releaseReminder.price
                    )}ETHでマーケットプレイスに出品しました。`
                  : `@${
                      releaseReminder.userName
                    } put ${creationName} in the Marketplace
              for ${weiToEth(releaseReminder.price)} ETH.`}
              </p>
              <p className={styles.notificationDateTime}>{displayTime}</p>
            </div>
          </div>
        </a>
      </Link>
    );
  }

  return null;
};

export default NotificationItem;
